/**
 * Firebase Cloud Messaging — token registration + foreground message handler.
 *
 * Call initFCM() once after the user logs in. It will:
 *  1. Request browser Notification permission
 *  2. Register the service worker
 *  3. Get the FCM token and save it to the backend
 *  4. Listen for foreground push messages and call onForegroundMessage
 *
 * If the Firebase config still contains placeholder values, or if the browser
 * doesn't support push (e.g. Safari < 16.4), the function exits silently.
 */
import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { firebaseConfig, VAPID_KEY } from '../config/firebaseConfig';
import { saveFcmToken } from '../services/notificationApi';

let _messaging = null;
let _unsubscribe = null;

function isConfigured() {
  return !firebaseConfig.apiKey.startsWith('REPLACE_');
}

function getFirebaseMessaging() {
  if (_messaging) return _messaging;
  try {
    const apps = getApps();
    const app = apps.length ? apps[0] : initializeApp(firebaseConfig);
    _messaging = getMessaging(app);
  } catch (e) {
    console.warn('[FCM] Firebase Messaging not available in this browser:', e.message);
    _messaging = null;
  }
  return _messaging;
}

/**
 * @param {(msg: { title: string, body: string, data: object }) => void} onForegroundMessage
 *   Called when a push arrives while the app tab is open and in focus.
 */
export async function initFCM(onForegroundMessage) {
  if (!isConfigured()) return;
  if (!('Notification' in window)) return;

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return;

    const messaging = getFirebaseMessaging();
    if (!messaging) return;

    // Register service worker explicitly so React dev server doesn't break it
    const swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });

    if (token) {
      await saveFcmToken(token).catch(() => {});
    }

    // Remove previous listener if initFCM is called again (e.g. re-login)
    if (_unsubscribe) _unsubscribe();

    _unsubscribe = onMessage(messaging, (payload) => {
      if (typeof onForegroundMessage === 'function') {
        onForegroundMessage({
          title: payload.notification?.title || '',
          body:  payload.notification?.body  || '',
          data:  payload.data || {},
        });
      }
    });
  } catch (e) {
    console.warn('[FCM] init failed:', e.message);
  }
}

/** Call on logout to clean up the foreground listener. */
export function teardownFCM() {
  if (_unsubscribe) {
    _unsubscribe();
    _unsubscribe = null;
  }
}
